import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Wifi, WifiOff, ChevronDown, Clock, Eye, Heart } from 'lucide-react';
import { isForceOffline, setForceOffline } from '../lib/forceOffline';
import { getCounts } from '../lib/syncQueue';
import { useProfile } from '../context/ProfileContext';
import { isViewAsEgnite, setViewAsEgnite } from '../hooks/useTierState';
import {
  DEV_TOPUP_PICKER_CHANGED,
  isDevTopupPickerEnabled,
  setDevTopupPickerEnabled,
} from '../lib/devTopupPicker';

/**
 * DeveloperSettings — collapsed-by-default panel surfacing tester / debug
 * controls. Currently exposes:
 *   - Force-offline toggle (simulates a disconnection for sync queue testing)
 *   - Live queue counts (pending / failed) so testers can see queue state
 *     without opening DevTools
 *   - Trial countdown shortcuts (set onboardingCompletedAt to N days ago)
 *   - View-as-Egnite override (free-tier UX preview)
 *   - Pricing top-up picker preview (IronLimb subscribe flow)
 *
 * Visual cost: one collapsed row (~36px) until expanded. Hidden behind a
 * disclosure summary so non-technical users won't notice it exists.
 */
const DAY_MS = 1000 * 60 * 60 * 24;
const TRIAL_DISMISS_KEY = 'enferno_trial_banner_dismissed_session';

const DeveloperSettings = () => {
  const [open, setOpen] = useState(false);
  const [offline, setOffline] = useState(isForceOffline());
  const [counts, setCounts] = useState({ pending: 0, failed: 0 });
  const [lastTrialClick, setLastTrialClick] = useState(null);
  const [viewAsEgnite, setViewAsEgniteState] = useState(isViewAsEgnite());
  const [topupPicker, setTopupPicker] = useState(isDevTopupPickerEnabled());
  const { update } = useProfile();

  useEffect(() => {
    const refresh = () => setOffline(isForceOffline());
    window.addEventListener('enferno:force-offline-changed', refresh);
    return () => window.removeEventListener('enferno:force-offline-changed', refresh);
  }, []);

  useEffect(() => {
    const refresh = () => setTopupPicker(isDevTopupPickerEnabled());
    window.addEventListener(DEV_TOPUP_PICKER_CHANGED, refresh);
    return () => window.removeEventListener(DEV_TOPUP_PICKER_CHANGED, refresh);
  }, []);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const tick = async () => {
      try {
        const c = await getCounts();
        if (alive) setCounts(c);
      } catch { /* idb not ready */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [open]);

  const toggle = () => {
    const next = !offline;
    setForceOffline(next);
    setOffline(next);
  };

  // Set onboardingCompletedAt to N days ago so the TrialBanner reflects that
  // many days of trial elapsed. Also clears the per-session dismiss so the
  // banner reappears immediately for verification.
  const setTrialDaysElapsed = (preset) => {
    const ts = new Date(Date.now() - preset.days * DAY_MS).toISOString();
    update({ onboardingCompletedAt: ts, completedOnboarding: true });
    try { sessionStorage.removeItem(TRIAL_DISMISS_KEY); } catch { /* ignore */ }
    setLastTrialClick(preset.label);
    toast.success(`Trial set to ${preset.label} (${preset.sub}). Reload Progress to see the banner.`);
    setTimeout(() => setLastTrialClick((current) => (current === preset.label ? null : current)), 2500);
  };

  const toggleViewAsEgnite = () => {
    const next = !viewAsEgnite;
    setViewAsEgnite(next);
    setViewAsEgniteState(next);
    toast.success(next
      ? 'Viewing as Egnite. Library and other gated UI will render the free-tier paywall.'
      : 'Restored to your real tier state.');
  };

  const toggleTopupPicker = () => {
    const next = !topupPicker;
    setDevTopupPickerEnabled(next);
    setTopupPicker(next);
    toast.success(next
      ? 'Pricing top-up picker ON. Open Pricing to preview subscribe flow.'
      : 'Pricing top-up picker off — Stripe carousel optional_items restored.');
  };

  const trialPresets = [
    { label: 'T-1d', sub: '13 days left', days: 1 },
    { label: 'T-11d', sub: '3 days · urgent', days: 11 },
    { label: 'T-13d', sub: '1 day · urgent', days: 13 },
    { label: 'T-14d', sub: 'Expired', days: 14 },
    { label: 'Reset', sub: 'Today', days: 0 },
  ];

  return (
    <div className="mt-8 bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden" data-testid="developer-settings-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-6 py-3 flex items-center justify-between text-left hover:bg-enf-surface-mid/40 transition-colors"
        data-testid="developer-settings-toggle"
      >
        <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold">Developer settings</span>
        <ChevronDown className={`w-4 h-4 text-enf-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-6 pb-5 pt-1 space-y-4 border-t border-enf-surface-border">
          {/* Force offline toggle */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              {offline ? <WifiOff className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> : <Wifi className="w-4 h-4 text-enf-text-muted mt-0.5 flex-shrink-0" />}
              <div>
                <div className="text-sm font-semibold">Simulate offline</div>
                <p className="text-xs text-enf-text-muted mt-0.5 max-w-md">
                  Fakes a disconnection at the network layer so you can test the offline queue without losing real connectivity. Equivalent to <code className="text-enf-role-info">?offline=force</code> in the URL.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggle}
              data-testid="developer-force-offline-toggle"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex-shrink-0 ${
                offline
                  ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40'
                  : 'bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-secondary border border-enf-surface-border'
              }`}
            >
              {offline ? 'On' : 'Off'}
            </button>
          </div>

          {/* Live queue counts */}
          <div className="text-xs text-enf-text-muted border-t border-enf-surface-border pt-3">
            Sync queue: <span className="text-enf-text-secondary">{counts.pending} pending</span>
            {' · '}
            <span className={counts.failed > 0 ? 'text-amber-300' : 'text-enf-text-secondary'}>{counts.failed} failed</span>
          </div>

          {/* Trial countdown shortcuts */}
          <div className="border-t border-enf-surface-border pt-3">
            <div className="flex items-start gap-3 mb-2">
              <Clock className="w-4 h-4 text-enf-text-muted mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold">Trial countdown</div>
                <p className="text-xs text-enf-text-muted mt-0.5 max-w-md">
                  Sets <code className="text-enf-role-info">onboardingCompletedAt</code> to N days ago so you can verify the TrialBanner&apos;s normal vs. final-countdown vs. expired states. Reload Progress after picking.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 ml-7">
              {trialPresets.map((p) => {
                const active = lastTrialClick === p.label;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setTrialDaysElapsed(p)}
                    data-testid={`developer-trial-${p.label.toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-md text-xs text-left transition-colors border ${
                      active
                        ? 'bg-enf-accent/20 text-enf-accent-fg border-enf-accent-fg/60'
                        : 'bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-secondary border-enf-surface-border hover:border-enf-surface-border'
                    }`}
                  >
                    <div className="font-semibold">{p.label}</div>
                    <div className="text-[10px] text-enf-text-muted">{p.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* View-as-Egnite override — admin/QA preview of the free-tier UX
              without touching role/tier/trial. Pure client-side; backend
              state is unchanged. */}
          <div className="border-t border-enf-surface-border pt-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <Eye className={`w-4 h-4 mt-0.5 flex-shrink-0 ${viewAsEgnite ? 'text-enf-accent-fg' : 'text-enf-text-muted'}`} />
                <div>
                  <div className="text-sm font-semibold">View as Egnite (free tier)</div>
                  <p className="text-xs text-enf-text-muted mt-0.5 max-w-md">
                    Client-side override that makes useTierState() report free-tier state regardless of admin / trial / subscription. Lets you verify the Library lock overlay, paywall modals, and other gated UI without changing role or tier. Toggle off to restore.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleViewAsEgnite}
                data-testid="developer-view-as-egnite-toggle"
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex-shrink-0 ${
                  viewAsEgnite
                    ? 'bg-enf-accent/20 text-enf-accent-fg border border-enf-accent-fg/60'
                    : 'bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-secondary border border-enf-surface-border'
                }`}
              >
                {viewAsEgnite ? 'On' : 'Off'}
              </button>
            </div>
          </div>

          {/* Pricing top-up picker — preview Enferno-side IronLimb choice
              before replacing Stripe optional_items carousel. */}
          <div className="border-t border-enf-surface-border pt-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${topupPicker ? 'text-enf-accent-fg' : 'text-enf-text-muted'}`} />
                <div>
                  <div className="text-sm font-semibold">Pricing top-up picker</div>
                  <p className="text-xs text-enf-text-muted mt-0.5 max-w-md">
                    Subscribe on{' '}
                    <Link to="/pricing" className="text-enf-accent-fg underline">Pricing</Link>
                    {' '}opens a required-choice top-up modal before Stripe (no default). Checkout uses line items, not the Stripe carousel. Device-local until you ship for everyone.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleTopupPicker}
                data-testid="developer-topup-picker-toggle"
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex-shrink-0 ${
                  topupPicker
                    ? 'bg-enf-accent/20 text-enf-accent-fg border border-enf-accent-fg/60'
                    : 'bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-secondary border border-enf-surface-border'
                }`}
              >
                {topupPicker ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperSettings;
